/** Share links for a document: list with expiry, copy and revoke; create a new link unless the page is private */

import { useId, useState } from 'react'
import { toast } from 'sonner'
import { Check, Copy, Link2, Plus, Trash2 } from 'lucide-react'
import type { AccessLevel } from '@agentwiki/shared'
import {
  isShareLinkExpired,
  shareLinkUrl,
  useCreateShareLink,
  useDeleteShareLink,
  useShareLinks,
  type ShareLink,
} from '../../hooks/use-share-links'
import { useAppStore } from '../../stores/app-store'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { buttonSecondaryClass, errorTextClass, hintClass, iconButtonClass } from '../ui/styles'

interface ShareLinkDisplayProps {
  documentId: string
  /** Current access level; new links are not offered for a private page. Omitted: creating is allowed. */
  accessLevel?: AccessLevel
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function ShareLinkDisplay({ documentId, accessLevel }: ShareLinkDisplayProps) {
  const t = useT()
  const headingId = useId()
  const locale = useAppStore((s) => s.locale)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data, isLoading, isError, refetch } = useShareLinks(documentId)
  const createLink = useCreateShareLink()
  const deleteLink = useDeleteShareLink()

  const links = data?.links ?? []
  const canCreate = accessLevel !== 'private'
  const origin = window.location.origin
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })

  const handleCreate = async () => {
    try {
      await createLink.mutateAsync({ documentId })
      toast.success(t('sharing.links.created'))
    } catch (err) {
      toast.error(t('sharing.links.createFailed', { message: errorMessage(err) }))
    }
  }

  const handleCopy = async (link: ShareLink) => {
    try {
      await navigator.clipboard.writeText(shareLinkUrl(link.token, origin))
      setCopiedId(link.id)
      setTimeout(() => setCopiedId((current) => (current === link.id ? null : current)), 2000)
    } catch {
      toast.error(t('sharing.copyFailed'))
    }
  }

  const handleRevoke = async (link: ShareLink) => {
    if (!window.confirm(t('sharing.links.revokeConfirm'))) return
    try {
      await deleteLink.mutateAsync({ id: link.id, documentId })
      toast.success(t('sharing.links.revoked'))
    } catch (err) {
      toast.error(t('sharing.links.revokeFailed', { message: errorMessage(err) }))
    }
  }

  const openLinkCount = links.filter((link) => !isShareLinkExpired(link.expiresAt)).length

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h4 id={headingId} className="flex items-center gap-1.5 text-sm font-medium text-fg">
        <Link2 className="h-4 w-4 text-fg-subtle" aria-hidden="true" />
        {t('sharing.links.title')}
      </h4>
      <p className={hintClass}>{canCreate ? t('sharing.links.hint') : t('sharing.links.privateHint')}</p>
      {/* Share tokens keep working whatever the access level, so a private page can still be open through old links */}
      {!canCreate && openLinkCount > 0 && (
        <p role="status" className="rounded-md border border-warning/30 bg-warning-soft px-2 py-1.5 text-xs text-warning">
          {t('sharing.links.stillOpen', { count: openLinkCount })}
        </p>
      )}

      {isLoading ? (
        <p className="text-xs text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <div role="alert" className={cn(errorTextClass, 'text-xs')}>
          {t('sharing.links.loadError')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : links.length === 0 ? (
        <p className="text-xs text-fg-subtle">{t('sharing.links.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => {
            const url = shareLinkUrl(link.token, origin)
            const expired = isShareLinkExpired(link.expiresAt)
            return (
              <li key={link.id} className="rounded-md border border-border bg-surface px-2 py-1.5">
                <div className="flex items-center gap-1">
                  <span className={cn('min-w-0 flex-1 truncate text-xs', expired ? 'text-fg-subtle line-through' : 'text-fg-muted')} title={url}>
                    {url}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleCopy(link)}
                    disabled={expired}
                    aria-label={t('sharing.links.copy')}
                    title={t('sharing.links.copy')}
                    className={iconButtonClass}
                  >
                    {copiedId === link.id ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRevoke(link)}
                    disabled={deleteLink.isPending}
                    aria-label={t('sharing.links.revoke')}
                    title={t('sharing.links.revoke')}
                    className={cn(iconButtonClass, 'text-danger hover:bg-danger-soft hover:text-danger')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className={cn('text-xs', expired ? 'text-danger' : 'text-fg-subtle')}>
                  {link.expiresAt
                    ? t(expired ? 'sharing.links.expired' : 'sharing.links.expires', { date: dateFormat.format(new Date(link.expiresAt)) })
                    : t('sharing.links.noExpiry')}
                </p>
              </li>
            )
          })}
        </ul>
      )}

      {canCreate && (
        <button type="button" onClick={() => void handleCreate()} disabled={createLink.isPending} className={cn(buttonSecondaryClass, 'w-full')}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {createLink.isPending ? t('sharing.links.creating') : t('sharing.links.create')}
        </button>
      )}
    </section>
  )
}
